<script lang="js" setup>
import { ref, computed, onMounted } from "vue";
import { fetchGetApi } from "@/util/api";
import * as XLSX from 'xlsx';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-default.css';
import { defineAsyncComponent } from 'vue';

const allData = ref([]);
const elementsPerPage = ref(25);
const currentPage = ref(1);
const filterQuery = ref("");
const fromDate = ref("");
const toDate = ref("");
const toast = useToast();
const siddarTypeFilter = ref("");
const managerFilter = ref("");

const viewSiddar = defineAsyncComponent(() => import("@/pages/operation/modal/viewSiddar.vue"));
const isModalVisible = ref(false);
const selectedItem = ref(null);

const getAllData = async () => {
    try {
        const response = await fetchGetApi('api/siddar/get/all');
        if (response && response.data) {
            allData.value = response.data;
        }
    } catch (error) {
        toast.error('Error fetching data');
    }
};

onMounted(async () => {
    await getAllData();
});

function downloadExcel() {
    const worksheet = XLSX.utils.json_to_sheet(allData.value);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Siddar Data");
    XLSX.writeFile(workbook, 'siddar_data.xlsx');
}

const filteredData = computed(() => {
    const query = filterQuery.value.toLowerCase();
    let filtered = allData.value;

    // Apply date range filter
    if (fromDate.value && toDate.value) {
        const from = new Date(fromDate.value);
        const to = new Date(toDate.value);
        to.setHours(23, 59, 59, 999); // Set to end of day

        filtered = filtered.filter(item => {
            const createdDate = new Date(item.createdAt);
            return createdDate >= from && createdDate <= to;
        });
    }

    // Apply Siddar Type filter
    if (siddarTypeFilter.value) {
        filtered = filtered.filter(item =>
            item.type?.toLowerCase() === siddarTypeFilter.value.toLowerCase()
        );
    }

    // Apply Manager filter
    if (managerFilter.value) {
        filtered = filtered.filter(item =>
            item.manager?.toLowerCase() === managerFilter.value.toLowerCase()
        );
    }

    // Apply text search filter
    return filtered.filter(item =>
        item.siteName?.toLowerCase().includes(query) ||
        item.department?.toLowerCase().includes(query) ||
        item.manager?.toLowerCase().includes(query) ||
        item.employee?.toLowerCase().includes(query)
    );
});

const paginatedData = computed(() => {
    const start = (currentPage.value - 1) * elementsPerPage.value;
    const end = start + elementsPerPage.value;
    return filteredData.value.slice(start, end);
});

function num_pages() {
    return Math.ceil(filteredData.value.length / elementsPerPage.value);
}

function change_page(page) {
    currentPage.value = page;
}

function previousPage() {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
}

function nextPage() {
    if (currentPage.value < num_pages()) {
        currentPage.value++;
    }
}

function showModal(item) {
    selectedItem.value = item;
    isModalVisible.value = true;
}

function hideModal() {
    isModalVisible.value = false;
    selectedItem.value = null;
}

const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
};

function clearDateFilters() {
    fromDate.value = "";
    toDate.value = "";
}
</script>

<template>
    <Breadcrumbs main="Apps" title="SIDDAR" />
    <div class="container-fluid">
        <div class="email-wrap bookmark-wrap">
            <div class="row card card-body">
                <div class="table-responsive product-table custom-scrollbar">
                    <form class="no-footer">
                        <div class="mb-3 row justify-content-between align-items-center">
                            <div class="col-12 col-md-auto mb-2 mb-md-0">
                                <label for="table-complete-search" class="col-form-label">Search:</label>
                                <input id="table-complete-search" type="text" class="form-control" name="searchTerm" v-model="filterQuery">
                            </div>
                            <div class="col-12 col-md-auto mb-2 mb-md-0 d-flex gap-2 align-items-center">
                                <div>
                                    <label for="from-date" class="col-form-label">From:</label>
                                    <input id="from-date" type="date" class="form-control" v-model="fromDate">
                                </div>
                                <div>
                                    <label for="to-date" class="col-form-label">To:</label>
                                    <input id="to-date" type="date" class="form-control" v-model="toDate">
                                </div>
                                <button type="button" class="btn btn-light mt-4" @click="clearDateFilters">
                                    Clear Dates
                                </button>
                            </div>
                            <div class="col-12 col-md-auto">
                                <button type="button" class="btn btn-primary" @click="downloadExcel">Download Excel</button>
                            </div>
                        </div>
                        <div v-if="allData.length === 0" class="text-center p-4">
                            <h4>No SIDDAR present</h4>
                        </div>
                        <div v-else class="table-responsive">
                            <table class="display table-striped table-hover table-bordered table" id="basic-1">
                                <thead>
                                    <tr>
                                        <th class="sorting_asc" scope="col" style="width: 50px;">SL</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Date</th>
                                        <th class="sorting_asc text-nowrap" scope="col">
                                            SIDDAR Type
                                            <select v-model="siddarTypeFilter" class="form-select form-select-sm mt-1" style="min-width: 100px;">
                                                <option value="">All</option>
                                                <option v-for="type in Array.from(new Set(allData.map(i => i.type).filter(Boolean)))" :key="type" :value="type">{{ type }}</option>
                                            </select>
                                        </th>
                                        <th class="sorting_asc text-nowrap" scope="col">Vacant Type</th>
                                        <th class="sorting_asc text-nowrap" scope="col">
                                            Name of Manager
                                            <select v-model="managerFilter" class="form-select form-select-sm mt-1" style="min-width: 100px;">
                                                <option value="">All</option>
                                                <option v-for="manager in Array.from(new Set(allData.map(i => i.manager).filter(Boolean)))" :key="manager" :value="manager">{{ manager }}</option>
                                            </select>
                                        </th>
                                        <th class="sorting_asc text-nowrap" scope="col">Site Name</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Employee Name</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Gender</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Designation</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Department</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Approx Working Days</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Dress Return</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Reason of No Return</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Amount Lost</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Type of Leaving</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Reason of Leaving</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Ops Deadline</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Recruitment Deadline</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Position Status</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Cancelled</th>
                                        <th class="sorting_asc text-nowrap" scope="col">Cancellation Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in paginatedData" :key="item._id">
                                        <td>{{ (currentPage - 1) * elementsPerPage + index + 1 }}</td>
                                        <td>{{ formatDate(item.createdAt) }}</td>
                                        <td>{{ item.type }}</td>
                                        <td>{{ item.vacentType }}</td>
                                        <td>{{ item.manager || '-' }}</td>
                                        <td>{{ item.siteName }}</td>
                                        <td>{{ item.employee }}</td>
                                        <td>{{ item.gender }}</td>
                                        <td>{{ item.designation }}</td>
                                        <td>{{ item.department }}</td>
                                        <td>{{ item.daysWorked }}</td>
                                        <td>{{ item.dressReturn ? 'Yes' : 'No' }}</td>
                                        <td>{{ item.reasonOfNoReturn || '-' }}</td>
                                        <td>{{ item.amountLost || '-' }}</td>
                                        <td>{{ item.typeOfLeaving }}</td>
                                        <td>{{ item.reason }}</td>
                                        <td>{{ formatDate(item.deadline) }}</td>
                                        <td>{{ formatDate(item.recuitmentDeadline) || '-' }}</td>
                                        <td>{{ item.positionStatus || 'Vacant' }}</td>
                                        <td>{{ item.isCancelled ? 'Yes' : 'No' }}</td>
                                        <td>{{ item.cancellationReason || '-' }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <ul class="pagination py-2 justify-content-end pagination-primary">
                            <li class="page-item" @click="previousPage">
                                <a class="page-link">Previous</a>
                            </li>
                            <li class="page-item" v-for="i in num_pages()" :key="i" :class="[i == currentPage ? 'active' : '']" @click="change_page(i)">
                                <a class="page-link">{{ i }}</a>
                            </li>
                            <li class="page-item" @click="nextPage">
                                <a class="page-link">Next</a>
                            </li>
                        </ul>
                    </form>
                    <viewSiddar v-if="isModalVisible" :show="isModalVisible" :item="selectedItem" @close="hideModal" @refreshData="onMounted" />
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.table-responsive {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
}

@media (max-width: 768px) {
    .table {
        display: block;
        width: 100%;
    }
    .form-select {
        width: 100% !important;
        min-width: 80px;
    }
    .pagination {
        flex-wrap: wrap;
        justify-content: center;
    }
    .col-md-auto {
        width: 100%;
    }
    .d-flex.gap-2 {
        flex-wrap: wrap;
    }
    .d-flex.gap-2 > div {
        width: 100%;
    }
}

.table th, .table td {
    white-space: nowrap;
    min-width: 100px;
    padding: 0.5rem;
    font-size: 0.875rem;
}

@media (max-width: 576px) {
    .table th, .table td {
        font-size: 0.75rem;
        padding: 0.25rem;
    }
    .form-select {
        font-size: 0.75rem;
        min-width: 60px;
    }
}
</style>